import {
  HydraulicBackfillSpecificationDto,
  PasteBackfillSpecificationDto,
  RheologyDataDto,
  ThroughputControlType,
  UcsCoefficientsDto
} from 'providers/api';
import { clampToRange, round, sanitiseNumber } from 'utils';

export type LimitBound = 'lower' | 'upper';

export interface RecipeLimitFormatters {
  ucs: (kPa: number, bound: LimitBound) => string;
  pumpPressure: (bar: number, bound: LimitBound) => string;
}

type NumberFormatter = (value: number | undefined, decimalPlaces: number) => string;

const LIMIT_DECIMAL_PLACES = 2;

export const unformattedLimits: RecipeLimitFormatters = {
  ucs: (kPa) => `${kPa}`,
  pumpPressure: (bar) => `${bar}`,
};

// Rounded towards the valid side, so the stated limit always passes the check it describes.
export const roundLimit = (value: number, bound: LimitBound, decimalPlaces = LIMIT_DECIMAL_PLACES) => {
  const scaled = round(value * (10 ** decimalPlaces), 6);
  return (bound === 'lower' ? Math.ceil(scaled) : Math.floor(scaled)) / (10 ** decimalPlaces);
};

export const formatLimit = (displayValue: number, bound: LimitBound, abbreviation: string, formatNumber: NumberFormatter) => {
  const limit = roundLimit(displayValue, bound);
  const decimalPlaces = [0, 1].find((places) => round(limit, places) === limit) ?? LIMIT_DECIMAL_PLACES;
  return `${formatNumber(limit, decimalPlaces)} ${abbreviation}`.trim();
};

export const pumpPressureWithinMax = (pumpPressure: number, maxPumpPressure: number) => (
  maxPumpPressure > 0 ? Math.min(pumpPressure, maxPumpPressure) : pumpPressure
);

const sortedTargetDays = (ucsCoefficients: { [key: string]: UcsCoefficientsDto } | undefined) => (
  Object.keys(ucsCoefficients ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b))
);

// UCS coefficient keys are strings; the form holds the first one as-is until the user picks a number.
export const firstTargetDays = (ucsCoefficients: { [key: string]: UcsCoefficientsDto } | undefined) => (
  sortedTargetDays(ucsCoefficients)[0] as unknown as number
);

const hasTargetDays = (ucsCoefficients: { [key: string]: UcsCoefficientsDto } | undefined, targetDays: unknown) => (
  sortedTargetDays(ucsCoefficients).some((days) => parseFloat(days) === Number(targetDays))
);

// A new recipe takes the new dataset's defaults; an existing one keeps what was entered, pulled inside the new limits.
export const pasteSpecificationForDataset = (
  specification: PasteBackfillSpecificationDto | undefined,
  { pumpPressure, tonnage, ucsCoefficients }: RheologyDataDto,
  keepEntered: boolean,
): PasteBackfillSpecificationDto => {
  const enteredPumpPressure = keepEntered ? sanitiseNumber(specification?.pumpPressure) : undefined;
  const enteredThroughput = keepEntered ? sanitiseNumber(specification?.throughput) : undefined;
  const targetDays = specification?.targetDays;

  return {
    targetUCSStrength: specification?.targetUCSStrength ?? 0,
    throughputControlType: specification?.throughputControlType ?? ThroughputControlType.DryTonnage,
    pumpPressure: pumpPressureWithinMax(Math.max(enteredPumpPressure ?? pumpPressure.default, 0), pumpPressure.max),
    throughput: enteredThroughput === undefined ? tonnage.default : clampToRange(enteredThroughput, tonnage.min, tonnage.max),
    targetDays: targetDays !== undefined && hasTargetDays(ucsCoefficients, targetDays) ? targetDays : firstTargetDays(ucsCoefficients),
  };
};

// An unset hydraulic specification already falls back to the selected dataset's limits in the inputs.
export const hydraulicSpecificationForDataset = (
  specification: HydraulicBackfillSpecificationDto | undefined,
  { tonnage }: RheologyDataDto,
  keepEntered: boolean,
): HydraulicBackfillSpecificationDto | undefined => {
  if (!specification) return specification;
  const enteredThroughput = keepEntered ? sanitiseNumber(specification.throughput) : undefined;

  return {
    ...specification,
    throughput: enteredThroughput === undefined ? tonnage.default : clampToRange(enteredThroughput, tonnage.min, tonnage.max),
  };
};
